<template>
    <div class="main-cn insight-page" :class="{'assistant-open': assistantVisible, 'assistant-pinned': assistantPinned}">
        <div class="insight-wrap" v-loading="loading">
            <div class="insight-crumb">
                <span>客户管理</span>
                <i class="el-icon-arrow-right"></i>
                <router-link to="/customers/high-value">智能客户洞察</router-link>
                <i class="el-icon-arrow-right"></i>
                <span class="crumb-current">{{ header.customer_name || '—' }}</span>
            </div>

            <div class="insight-hero">
                <div class="insight-kicker">
                    <i class="el-icon-star-on"></i>
                    CUSTOMER INTELLIGENCE
                </div>
                <div class="insight-top">
                    <div class="title-wrap">
                        <span class="title-mark">
                            <i class="el-icon-s-opportunity"></i>
                        </span>
                        <span class="page-title">智能客户洞察</span>
                        <span class="customer-name">{{ header.customer_name || '—' }}</span>
                    </div>
                    <div class="top-actions">
                        <el-button v-if="actions.can_claim" class="btn-claim" size="small" icon="el-icon-s-flag" @click="claimCustomer">领取客户</el-button>
                        <el-button class="btn-follow" size="small" icon="el-icon-phone-outline" @click="goFollow">客户跟进</el-button>
                    </div>
                </div>

                <div class="insight-banner" :class="{'is-generating': isAiGenerating, 'is-failed': header.ai_status === 'failed'}">
                    <div class="banner-left">
                        <span class="banner-icon"><i class="el-icon-s-opportunity"></i></span>
                        <span v-if="isAiGenerating" class="banner-generating">
                            <i class="el-icon-loading"></i>
                            {{ generatingWaitText }}
                        </span>
                        <span v-else-if="header.ai_status === 'failed'">{{ header.ai_message || '洞察生成失败，当前先展示CRM数据' }}</span>
                        <span v-else>您好，客户的<em>智能洞察</em>已生成，帮您一眼掌握重点，把握成交时间。</span>
                    </div>
                    <div class="banner-right">
                        <span v-if="isAiGenerating">正在生成…</span>
                        <span v-else-if="header.ai_status === 'failed'">生成失败，可点击刷新重试</span>
                        <span v-else>内容由AI生成于 {{ header.generated_at || '—' }}</span>
                        <el-tooltip :content="canRefresh ? '刷新智能洞察' : '该客户今天已经刷新过，明天再试'" placement="bottom">
                            <span>
                                <el-button type="text" class="banner-refresh" icon="el-icon-refresh" :loading="refreshing" :disabled="!canRefresh" @click="refreshInsight"></el-button>
                            </span>
                        </el-tooltip>
                    </div>
                </div>
            </div>

            <div class="overview-cards">
                <div v-for="card in overviewCards" :key="card.code" class="ov-card" :class="'ov-' + card.code">
                    <div class="ov-icon"><i :class="cardIcon(card.code)"></i></div>
                    <div class="ov-body">
                        <div class="ov-name">
                            {{ card.name }}
                            <span v-if="card.ai" class="ai-badge">+ AI</span>
                        </div>
                        <div class="ov-value">
                            <span>{{ cardValue(card) }}</span>
                            <em v-if="cardValue(card) !== '生成中' && cardValue(card) !== '—'">{{ card.unit }}</em>
                        </div>
                        <div class="ov-extra">{{ card.extra }}</div>
                    </div>
                </div>
            </div>

            <div class="insight-body">
                <div class="insight-nav">
                    <div v-for="mod in visibleModules" :key="mod.code" class="nav-item" :class="{active: activeModule === mod.code}" @click="scrollTo(mod.code)">
                        <i :class="moduleIcon(mod.code)"></i>
                        <span>{{ mod.name }}</span>
                    </div>
                </div>
                <div class="insight-main">
                    <div id="mod-overview" class="mod-card mod-overview">
                        <div class="mod-head">
                            <div class="mod-head-left">
                                <span class="mod-icon"><i class="el-icon-office-building"></i></span>
                                <span class="mod-name">客户概览</span>
                            </div>
                        </div>
                        <table class="ov-table">
                            <tr>
                                <th>客户名称</th><td>{{ fieldText('name') }}</td>
                                <th>统一社会信用代码</th><td>{{ fieldText('credit_code') }}<span v-if="fieldAi('credit_code')" class="ai-badge">+ AI</span></td>
                                <th>法人姓名</th><td>{{ fieldText('legal_person') }}<span v-if="fieldAi('legal_person')" class="ai-badge">+ AI</span></td>
                                <th>电话</th><td>{{ fieldText('phone') }}<span v-if="fieldAi('phone')" class="ai-badge">+ AI</span></td>
                            </tr>
                            <tr>
                                <th>所在地区</th><td>{{ fieldText('region') }}</td>
                                <th>学段</th><td>{{ fieldText('stage') }}</td>
                                <th>办学性质</th><td>{{ fieldText('school_type') }}<span v-if="fieldAi('school_type')" class="ai-badge">+ AI</span></td>
                                <th>客户类型</th><td>{{ fieldText('customer_type') }}</td>
                            </tr>
                            <tr>
                                <th>详细地址</th><td>{{ fieldText('address') }}<span v-if="fieldAi('address')" class="ai-badge">+ AI</span></td>
                                <th>客户级别</th><td>{{ fieldText('level') }}</td>
                                <th>学生数</th><td>{{ fieldWithUnit('student_num', '人') }}<span v-if="fieldAi('student_num')" class="ai-badge">+ AI</span></td>
                                <th>班级数</th><td>{{ fieldWithUnit('class_num', '个') }}<span v-if="fieldAi('class_num')" class="ai-badge">+ AI</span></td>
                            </tr>
                            <tr>
                                <th>教师数</th><td>{{ fieldWithUnit('teacher_num', '人') }}<span v-if="fieldAi('teacher_num')" class="ai-badge">+ AI</span></td>
                                <th>最后跟进时间</th><td colspan="5">{{ fieldText('last_trace_at') }}</td>
                            </tr>
                        </table>
                    </div>

                    <div id="mod-contacts" class="mod-card mod-contacts">
                        <div class="mod-head">
                            <div class="mod-head-left">
                                <span class="mod-icon"><i class="el-icon-user"></i></span>
                                <span class="mod-name">对接人与决策链</span>
                                <span class="ai-badge">+ AI</span>
                                <span v-if="isAiGenerating" class="ai-wait-badge">生成中</span>
                            </div>
                        </div>
                        <div v-if="contacts.length" class="contact-list">
                            <div v-for="item in contacts" :key="item.id" class="contact-card" :class="'role-' + item.role">
                                <div class="contact-head">
                                    <span class="contact-name">{{ item.name || '—' }}</span>
                                    <span v-if="item.name_ai" class="ai-badge">+ AI</span>
                                    <span class="role-tag">{{ item.role_name }}</span>
                                </div>
                                <div class="contact-job">{{ item.job_name || '—' }}</div>
                                <div class="phone-box">
                                    <i class="el-icon-phone-outline"></i>
                                    <span>{{ item.display_mobile || item.mobile_masked || '暂无手机号' }}</span>
                                    <i v-if="item.can_view_mobile && item.mobile_masked && !item.display_mobile" class="el-icon-view" @click="showMobile(item)"></i>
                                </div>
                                <div class="contact-meta">
                                    <div>
                                        <span v-for="(p, pIndex) in (item.apply_products || [])" :key="pIndex" class="mini-chip">{{ p.product_name }}</span>
                                        <span v-if="!item.apply_products || !item.apply_products.length" class="muted">暂无意向产品</span>
                                    </div>
                                    <span class="meta-date"><i class="el-icon-time"></i> {{ item.created_at || '—' }}</span>
                                </div>
                                <div class="attitude-bar">
                                    <span>当前态度：<em>{{ attitudeEmoji(item.attitude) }}</em> {{ item.attitude || '—' }}</span>
                                    <span>已联系：<b>{{ item.contact_count || 0 }}</b>次</span>
                                </div>
                                <div v-if="item.attitude_changes && item.attitude_changes.length" class="timeline-box">
                                    <div class="timeline" :style="{height: timelineHeight + 'px'}">
                                        <div v-for="(change, cIndex) in item.attitude_changes" :key="cIndex" class="timeline-item">
                                            <span class="dot"></span>
                                            <span class="tl-label">{{ change.label || change.attitude || '—' }}</span>
                                            <span class="tl-time">{{ change.month || change.time || '' }}</span>
                                            <span class="tl-text">{{ change.summary || change.content || '' }}</span>
                                        </div>
                                    </div>
                                    <div class="timeline-resizer" @mousedown.prevent="startTimelineResize" title="上下拖动调整高度"></div>
                                </div>
                            </div>
                        </div>
                        <div v-else class="empty-box">{{ aiWaitText('暂无对接人') }}</div>
                    </div>

                    <div id="mod-nearby" class="mod-card mod-nearby">
                        <div class="mod-head">
                            <div class="mod-head-left">
                                <span class="mod-icon"><i class="el-icon-location-outline"></i></span>
                                <span class="mod-name">周边客户成交情况</span>
                                <a class="mod-link" href="javascript:;" @click="goDoneCustomers">成交客户</a>
                            </div>
                        </div>
                        <div v-if="nearby.city_name && (nearbyDistricts.length || nearbyProducts.length)" class="nearby-panels">
                            <div v-if="nearbyDistricts.length" class="nearby-panel">
                                <div class="nearby-panel-head">{{ nearby.city_name }}成交客户{{ nearby.total || 0 }}个</div>
                                <div class="nearby-panel-body">
                                    <div v-for="item in nearbyDistricts" :key="item.district_id || item.district_name" class="nearby-panel-row">
                                        <em>{{ item.district_name || '未知' }}</em>
                                        <span>{{ item.customer_count || 0 }}个</span>
                                    </div>
                                </div>
                            </div>
                            <div v-if="nearbyProducts.length" class="nearby-panel">
                                <div class="nearby-panel-head">已购产品{{ nearbyProducts.length }}种</div>
                                <div class="nearby-panel-body">
                                    <div v-for="item in nearbyProducts" :key="item.product_id || item.product_name" class="nearby-panel-row">
                                        <em>{{ item.product_name || '未知' }}</em>
                                        <span>{{ item.customer_count || 0 }}个客户</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-else class="empty-box">{{ nearby.summary_text || '暂无同城成交数据' }}</div>
                        <div class="sub-title"><span class="sub-ico">🏆</span>名校成交信息</div>
                        <div v-if="famousSchools.length" class="famous-list">
                            <div v-for="(school, index) in famousSchools" :key="index" class="famous-card">
                                <div class="famous-head">
                                    <span v-for="tag in school.tags" :key="tag" class="school-tag" :class="famousTagClass(tag)">{{ tag }}</span>
                                    <strong :title="school.customer_name">{{ school.customer_name }}</strong>
                                    <span class="product-tag" :class="productTagClass(index)">{{ school.product_name }}</span>
                                </div>
                                <div class="famous-line"><i class="el-icon-location-outline"></i> {{ school.region || '—' }}</div>
                                <div class="famous-line"><i class="el-icon-menu"></i> 班级数：{{ school.class_num || '—' }}</div>
                                <div class="famous-line"><i class="el-icon-date"></i> 合同周期：{{ school.contract_start || '—' }}至{{ school.contract_end || '—' }}</div>
                                <div class="famous-line"><i class="el-icon-time"></i> 最后成交：{{ school.last_deal_at || '—' }}</div>
                            </div>
                        </div>
                        <div v-else class="empty-box">暂无名校成交信息</div>
                    </div>

                    <div id="mod-trial" class="mod-card mod-trial">
                        <div class="mod-head">
                            <div class="mod-head-left">
                                <span class="mod-icon"><i class="el-icon-goods"></i></span>
                                <span class="mod-name">产品试用</span>
                                <a class="mod-link" href="javascript:;" @click="goProductInfo">产品信息</a>
                            </div>
                        </div>
                        <div v-if="trialOrders.length" class="trial-layout">
                            <div class="trial-table-wrap">
                                <div class="trial-caption">
                                    <i class="el-icon-time"></i> 试用订单
                                    <span v-if="trial.valid_text" class="trial-valid" :class="trial.valid ? 'is-valid' : 'is-invalid'">{{ trial.valid_text }}</span>
                                </div>
                                <table class="trial-table">
                                    <tr>
                                        <th>产品名称</th>
                                        <th>开通时间</th>
                                        <th>到期时间</th>
                                        <th>累计下载量</th>
                                        <th>近30天下载量</th>
                                        <th>用户登录数</th>
                                    </tr>
                                    <tr v-for="row in trialOrders" :key="row.product_id">
                                        <td>{{ row.product_name }}</td>
                                        <td>{{ row.start_at || '—' }}</td>
                                        <td>{{ row.end_at || '—' }}</td>
                                        <td>{{ row.total_download || 0 }}</td>
                                        <td>{{ row.download_30d || 0 }}</td>
                                        <td>{{ row.login_users != null ? row.login_users : (row.active_users || 0) }}</td>
                                    </tr>
                                </table>
                            </div>
                            <div class="trial-chart" v-if="hasTrendDownloads">
                                <div class="chart-title">{{ trial.trend && trial.trend.title ? trial.trend.title : '试用订单 · 近7天下载量' }}</div>
                                <svg v-if="trendPoints.length" class="line-svg" viewBox="0 0 320 160" preserveAspectRatio="none">
                                    <polyline :points="trendPolyline" fill="none" stroke="#22c55e" stroke-width="3"></polyline>
                                    <circle v-for="(pt, idx) in trendCircles" :key="idx" :cx="pt.x" :cy="pt.y" r="4" fill="#22c55e"></circle>
                                </svg>
                                <div class="chart-axis">
                                    <span v-for="point in trendPoints" :key="point.day">{{ point.day }}</span>
                                </div>
                            </div>
                        </div>
                        <div v-else class="empty-box">{{ trial.empty_text || '暂未开通试用' }}</div>
                    </div>

                    <div id="mod-signals" class="mod-card mod-signals">
                        <div class="mod-head">
                            <div class="mod-head-left">
                                <span class="mod-icon"><i class="el-icon-data-analysis"></i></span>
                                <span class="mod-name">意向与信号</span>
                                <span class="ai-badge">+ AI</span>
                                <span v-if="isAiGenerating" class="ai-wait-badge">生成中</span>
                            </div>
                        </div>
                        <div class="signal-grid">
                            <div class="signal-box signal-coop">
                                <div class="signal-title">合作信号</div>
                                <div v-if="signalList(signals.cooperation).length" class="chips">
                                    <span v-for="(t, i) in signalList(signals.cooperation)" :key="i" class="chip chip-green"><i></i>{{ t }}</span>
                                </div>
                                <div v-else class="muted">{{ signalEmptyText('暂无合作信号') }}</div>
                            </div>
                            <div class="signal-box signal-need">
                                <div class="signal-title">需求点</div>
                                <div v-if="signalList(signals.needs).length" class="chips">
                                    <span v-for="(t, i) in signalList(signals.needs)" :key="i" class="chip chip-purple"><i></i>{{ t }}</span>
                                </div>
                                <div v-else class="muted">{{ signalEmptyText('暂无需求点') }}</div>
                            </div>
                            <div class="signal-box signal-block">
                                <div class="signal-title">卡点</div>
                                <div v-if="signalList(signals.blocks).length" class="chips">
                                    <span v-for="(t, i) in signalList(signals.blocks)" :key="i" class="chip chip-red"><i></i>{{ t }}</span>
                                </div>
                                <div v-else class="muted">{{ signalEmptyText('暂无卡点') }}</div>
                            </div>
                            <div class="signal-box signal-comp">
                                <div class="signal-title">竞品信息</div>
                                <div v-if="signalList(signals.competitors).length" class="chips">
                                    <span v-for="(t, i) in signalList(signals.competitors)" :key="i" class="chip chip-green"><i></i>{{ t }}</span>
                                </div>
                                <div v-else class="muted">{{ signalEmptyText('暂无竞品比对记录') }}</div>
                            </div>
                        </div>
                    </div>

                    <div id="mod-strategy" class="mod-card mod-strategy">
                        <div class="mod-head">
                            <div class="mod-head-left">
                                <span class="mod-icon"><i class="el-icon-s-opportunity"></i></span>
                                <span class="mod-name">跟进策略</span>
                                <span class="ai-badge">+ AI</span>
                                <span v-if="isAiGenerating" class="ai-wait-badge">生成中</span>
                            </div>
                        </div>
                        <div v-if="strategy.length">
                            <div v-for="(item, index) in strategy" :key="index" class="strategy-item" :class="'strategy-' + (index % 3)">
                                <span class="strategy-no">{{ index + 1 }}</span>
                                <div>
                                    <strong>{{ item.title || item }}</strong>
                                    <p v-if="item.content || item.action">{{ item.content || item.action }}</p>
                                </div>
                            </div>
                        </div>
                        <div v-else class="empty-box">{{ aiWaitText('暂无跟进策略') }}</div>
                    </div>
                </div>
            </div>

            <div class="float-ops">
                <div v-if="actions.can_claim" class="fab fab-claim" @click="claimCustomer"><i class="el-icon-s-flag"></i> 领取客户</div>
                <div class="fab fab-follow" @click="goFollow"><i class="el-icon-phone-outline"></i> 客户跟进</div>
                <div v-show="!assistantVisible" class="fab fab-ai" @click="openAssistant"><i class="el-icon-chat-dot-round"></i> AI销售助手</div>
                <div class="fab-switch">
                    <div class="fab-arrow" :class="{disabled: !neighbors.prev_id}" @click="switchCustomer(neighbors.prev_id)"><i class="el-icon-arrow-up"></i></div>
                    <div class="fab-arrow" :class="{disabled: !neighbors.next_id}" @click="switchCustomer(neighbors.next_id)"><i class="el-icon-arrow-down"></i></div>
                </div>
            </div>

            <div class="assistant-panel" v-show="assistantVisible">
                <div class="assistant-head">
                    <div class="assistant-title">
                        <i class="el-icon-chat-dot-round"></i>
                        销售智能助手
                    </div>
                    <div class="assistant-head-ops">
                        <el-tooltip content="历史对话" placement="bottom">
                            <i class="el-icon-time" @click="toggleAssistantHistory"></i>
                        </el-tooltip>
                        <el-tooltip content="新对话" placement="bottom">
                            <i class="el-icon-plus" @click="newAssistantSession"></i>
                        </el-tooltip>
                        <el-tooltip :content="assistantPinned ? '取消固定' : '固定为右侧全高面板'" placement="bottom">
                            <i :class="assistantPinned ? 'el-icon-unlock' : 'el-icon-lock'" @click="assistantPinned = !assistantPinned"></i>
                        </el-tooltip>
                        <i class="el-icon-close" @click="closeAssistant"></i>
                    </div>
                </div>
                <div class="assistant-history" v-show="assistantHistoryVisible">
                    <div class="history-head">历史对话</div>
                    <div v-if="!assistantSessions.length" class="history-empty">暂无历史对话，发送后会自动保存</div>
                    <div
                        v-for="item in assistantSessions"
                        :key="item.id"
                        class="history-item"
                        :class="{active: item.id === assistantSessionId}"
                        @click="openAssistantSession(item)">
                        <div class="history-title">{{ item.title || '新对话' }}</div>
                        <div class="history-time">{{ formatAssistantTime(item.updated_at) }}</div>
                    </div>
                </div>
                <div class="assistant-msgs" ref="assistantMsgs">
                    <div class="assistant-welcome">
                        您好，我是销售智能助手。已自动带入当前客户基本信息、对接人、试用/到期和跟进记录。可点击「沟通策略」获取下一次沟通重点和话术，也可以直接提问。
                    </div>
                    <div v-for="msg in assistantMessages" :key="msg.id || msg._key" :class="['assistant-msg', 'msg-' + msg.role]">
                        <div class="msg-bubble">
                            <div class="msg-text">{{ msg.content }}</div>
                            <el-button v-if="msg.role === 'assistant'" type="text" size="mini" icon="el-icon-document-copy" @click="copyAssistant(msg)">复制</el-button>
                        </div>
                    </div>
                    <div v-if="assistantSending" class="assistant-msg msg-assistant">
                        <div class="msg-bubble thinking">正在生成回复...</div>
                    </div>
                </div>
                <div class="assistant-foot">
                    <el-button size="mini" type="primary" plain :disabled="assistantSending" @click="sendStrategy">沟通策略</el-button>
                    <div class="assistant-input">
                        <el-input
                            type="textarea"
                            :rows="2"
                            v-model="assistantInput"
                            placeholder="请输入要咨询的内容..."
                            :disabled="assistantSending"
                            @keydown.native="onAssistantKeydown">
                        </el-input>
                        <el-button type="primary" :loading="assistantSending" @click="sendAssistant()">发送</el-button>
                    </div>
                </div>
            </div>

            <el-dialog title="已达领取上限" :visible.sync="limitVisible" width="640px">
                <p>已达领取上限，请解绑高价值客户后再试</p>
                <el-table :data="boundList" size="small" border>
                    <el-table-column prop="name" label="客户名称"></el-table-column>
                    <el-table-column prop="class_num" label="班级数" width="90"></el-table-column>
                    <el-table-column prop="school_type_name" label="办学性质" width="90"></el-table-column>
                    <el-table-column label="操作" width="90">
                        <template slot-scope="scope">
                            <el-button type="text" @click="unbindCustomer(scope.row)">解绑</el-button>
                        </template>
                    </el-table-column>
                </el-table>
            </el-dialog>
        </div>
    </div>
</template>

<script>
import CustomerService from "~/services/customer";

export default {
    name: "CustomerInsight",
    data() {
        return {
            loading: false,
            refreshing: false,
            queueTip: '',
            assistantVisible: false,
            assistantPinned: false,
            assistantSending: false,
            assistantSessionId: 0,
            assistantInput: '',
            assistantMessages: [],
            assistantSessions: [],
            assistantHistoryVisible: false,
            limitVisible: false,
            boundList: [],
            activeModule: 'overview',
            info: {},
            aiPollTimer: null,
            aiPollNotified: false,
            timelineHeight: 260,
            timelineResizing: false,
        };
    },
    created() {
        this.getInfo();
    },
    beforeDestroy() {
        this.stopAiPoll();
        this.stopTimelineResize();
    },
    beforeRouteLeave(to, from, next) {
        this.stopAiPoll();
        next();
    },
    watch: {
        '$route.query.id': function (id) {
            if (!this.isInsightRoute()) {
                this.stopAiPoll();
                return;
            }
            if (!id) {
                return;
            }
            this.stopAiPoll();
            this.aiPollNotified = false;
            this.getInfo();
            this.resetAssistant(true);
        }
    },
    computed: {
        header() { return this.info.header || {}; },
        isAiGenerating() {
            const header = this.header || {};
            const ai = this.info.ai || {};
            const status = header.ai_status || ai.status || '';
            if (!header.customer_id && (this.loading || this.aiPollTimer)) {
                return true;
            }
            return !!(header.ai_generating || ai.generating || status === 'pending' || status === 'running');
        },
        generatingWaitText() {
            const count = Number(this.header.ai_queue_count || 0);
            const min = Number(this.header.ai_wait_min || 0);
            const max = Number(this.header.ai_wait_max || 0);
            if (count > 2 && min > 0 && max > 0) {
                return '当前有' + count + '个客户正在生成智能洞察，预计等待约 ' + min + '～' + max + ' 分钟，完成后会自动更新，无需刷新。';
            }
            return '智能洞察生成中，先看 CRM 数据，大约 3～4 分钟，完成后会自动更新，无需刷新。';
        },
        overview() { return this.info.overview || {}; },
        overviewCards() { return this.info.overview_cards || []; },
        contacts() { return this.info.contacts || []; },
        nearby() { return this.info.nearby || {}; },
        famousSchools() { return this.nearby.famous_schools || []; },
        trial() { return this.info.trial || {}; },
        trialOrders() { return this.trial.orders || []; },
        trendPoints() { return (this.trial.trend && this.trial.trend.points) ? this.trial.trend.points : []; },
        hasTrendDownloads() {
            return this.trendPoints.some(function (item) {
                return (item.num || 0) > 0;
            });
        },
        canRefresh() {
            if (this.isAiGenerating) {
                return false;
            }
            if (this.header.ai_status === 'failed') {
                return true;
            }
            return this.header.can_refresh !== false;
        },
        signals() { return this.info.signals || {}; },
        strategy() { return this.info.strategy || []; },
        actions() { return this.info.actions || {}; },
        neighbors() { return this.info.neighbors || {}; },
        visibleModules() {
            const modules = this.info.modules || [];
            return modules.filter(function (item) {
                return item.phase_one !== false;
            });
        },
        nearbyDistricts() { return this.nearby.districts || []; },
        nearbyProducts() { return this.nearby.products || []; },
        trendPolyline() {
            return this.trendCircles.map(function (pt) { return pt.x + ',' + pt.y; }).join(' ');
        },
        trendCircles() {
            const points = this.trendPoints;
            if (!points.length) return [];
            const width = 300;
            const height = 130;
            const padX = 16;
            const padY = 18;
            const nums = points.map(function (item) { return item.num || 0; });
            const max = Math.max.apply(null, nums.concat([1]));
            const step = points.length > 1 ? (width - padX * 2) / (points.length - 1) : 0;
            return points.map(function (item, index) {
                return {
                    x: padX + index * step,
                    y: height - padY - ((item.num || 0) / max) * (height - padY * 2)
                };
            });
        }
    },
    methods: {
        isInsightRoute() {
            return (this.$route.path || '').indexOf('/customers/insight') !== -1;
        },
        startTimelineResize(e) {
            this.timelineResizing = true;
            this._timelineResizeStartY = e.clientY;
            this._timelineResizeStartH = this.timelineHeight;
            window.addEventListener('mousemove', this.onTimelineResize);
            window.addEventListener('mouseup', this.stopTimelineResize);
        },
        onTimelineResize(e) {
            if (!this.timelineResizing) return;
            const next = this._timelineResizeStartH + (e.clientY - this._timelineResizeStartY);
            this.timelineHeight = Math.min(420, Math.max(88, next));
        },
        stopTimelineResize() {
            this.timelineResizing = false;
            window.removeEventListener('mousemove', this.onTimelineResize);
            window.removeEventListener('mouseup', this.stopTimelineResize);
        },
        getInfo(silent) {
            if (!this.isInsightRoute()) {
                this.stopAiPoll();
                return;
            }
            const id = this.$route.query.id;
            if (!id) {
                this.stopAiPoll();
                if (!silent) {
                    this.$message.error('缺少客户ID');
                }
                return;
            }
            const wasGenerating = this.isAiGenerating;
            if (!silent) {
                this.loading = true;
            }
            CustomerService.getHighValueView(id).then(res => {
                if (res.code == 0) {
                    this.info = res.data || {};
                    this.syncAiPoll();
                    if (silent && wasGenerating && !this.isAiGenerating && this.header.ai_status === 'success' && !this.aiPollNotified) {
                        this.aiPollNotified = true;
                        this.queueTip = '';
                        this.$message.success('智能洞察已生成');
                    }
                } else if (!silent) {
                    this.$message.error(res.msg || '获取洞察失败');
                    this.startAiPoll();
                }
                this.loading = false;
            }).catch(() => {
                this.loading = false;
                this.startAiPoll();
            });
        },
        syncAiPoll() {
            if (this.isAiGenerating) {
                this.startAiPoll();
            } else {
                this.stopAiPoll();
            }
        },
        startAiPoll() {
            if (this.aiPollTimer) {
                return;
            }
            const self = this;
            this.aiPollTimer = setInterval(function () {
                self.getInfo(true);
            }, 4000);
        },
        stopAiPoll() {
            if (this.aiPollTimer) {
                clearInterval(this.aiPollTimer);
                this.aiPollTimer = null;
            }
        },
        fieldText(key) {
            const field = this.overview[key];
            if (!field) return '—';
            if (field.value === null || field.value === '') {
                return this.fieldWaiting(key) ? '生成中' : '—';
            }
            return field.value;
        },
        fieldAi(key) {
            return !!(this.overview[key] && this.overview[key].ai);
        },
        fieldWaiting(key) {
            const aiKeys = ['credit_code', 'legal_person', 'phone', 'address', 'class_num', 'student_num', 'teacher_num'];
            return this.isAiGenerating && aiKeys.indexOf(key) >= 0;
        },
        aiWaitText(emptyText) {
            if (this.isAiGenerating) {
                return 'AI 正在生成，完成后自动更新';
            }
            if (this.header.ai_status === 'failed') {
                return '生成失败，可点击右上角刷新重试';
            }
            return emptyText;
        },
        fieldWithUnit(key, unit) {
            const value = this.fieldText(key);
            if (value === '—' || value === '生成中') return value;
            return value + unit;
        },
        signalList(list) {
            return list && list.length ? list : [];
        },
        attitudeEmoji(text) {
            const t = String(text || '');
            if (/支持|认可|兴趣|推动|配合|积极/.test(t)) return '😊';
            if (/验证|观望|未知|冷淡/.test(t)) return '😐';
            if (/拒绝|反感|存疑|反对/.test(t)) return '😟';
            return t ? '😐' : '';
        },
        famousTagClass(tag) {
            const t = String(tag || '');
            if (t.indexOf('市重点') >= 0) return 'tag-city';
            if (t.indexOf('区重点') >= 0 || t.indexOf('省重点') >= 0) return 'tag-district';
            return 'tag-school';
        },
        productTagClass(index) {
            return ['tag-prod-blue', 'tag-prod-cyan', 'tag-prod-green'][index % 3];
        },
        signalEmptyText(emptyText) {
            return this.aiWaitText(emptyText);
        },
        moduleIcon(code) {
            if (code === 'overview') return 'el-icon-office-building';
            if (code === 'contacts') return 'el-icon-user';
            if (code === 'nearby') return 'el-icon-location-outline';
            if (code === 'trial') return 'el-icon-goods';
            if (code === 'signals') return 'el-icon-data-analysis';
            if (code === 'strategy') return 'el-icon-s-opportunity';
            return 'el-icon-document';
        },
        applyText(list) {
            if (!list || !list.length) return '暂无意向产品';
            return list.map(function (item) { return item.product_name; }).join('、');
        },
        listText(list) {
            if (!list || !list.length) return this.aiWaitText('暂无内容');
            return list.join('、');
        },
        barHeight(num) {
            const nums = this.trendPoints.map(function (item) { return item.num || 0; });
            const max = Math.max.apply(null, nums.concat([1]));
            return Math.max(8, Math.round((num || 0) / max * 80));
        },
        cardIcon(code) {
            if (code === 'scale') return 'el-icon-menu';
            if (code === 'intention') return 'el-icon-data-analysis';
            return 'el-icon-time';
        },
        cardValue(card) {
            if (!card || card.value === null || card.value === '') {
                return (this.isAiGenerating && card.ai) ? '生成中' : '—';
            }
            return card.value;
        },
        scrollTo(code) {
            this.activeModule = code;
            const el = document.getElementById('mod-' + code);
            if (el) el.scrollIntoView({behavior: 'smooth', block: 'start'});
        },
        refreshInsight() {
            const id = this.$route.query.id;
            this.refreshing = true;
            CustomerService.refreshHighValueInsight(id).then(res => {
                this.refreshing = false;
                const queue = (res.data && res.data.refresh_queue) || {};
                if (res.code == 0) {
                    this.info = res.data || this.info;
                    if (this.info.header) {
                        this.$set(this.info.header, "can_refresh", false);
                    }
                    this.aiPollNotified = false;
                    if (queue.busy) {
                        this.queueTip = res.msg || '';
                        this.$message.warning(res.msg);
                    } else {
                        this.queueTip = '';
                        this.$message.success(res.msg || '????');
                    }
                    this.syncAiPoll();
                } else {
                    this.$message.warning(res.msg || '刷新失败');
                    if (res.data && res.data.reason === 'daily_limit' && this.info.header) {
                        this.$set(this.info.header, 'can_refresh', false);
                    }
                    if (res.data && res.data.reason === 'in_queue') {
                        this.startAiPoll();
                    }
                }
            }).catch((err) => {
                this.refreshing = false;
                const msg = (err && (err.msg || err.message)) || '';
                if (msg) {
                    this.$message.warning(msg);
                }
                this.startAiPoll();
            });
        },
        claimCustomer() {
            const self = this;
            this.$confirm(this.actions.claim_confirm_text || '确定要绑定该客户吗', '提示', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(function () {
                CustomerService.claimHighValueCustomer(self.$route.query.id).then(function (res) {
                    if (res.code == 0) {
                        self.$message.success(res.msg || '领取成功');
                        self.getInfo();
                    } else if (res.data && res.data.reached_limit) {
                        self.boundList = res.data.list || [];
                        self.limitVisible = true;
                    }
                });
            }).catch(function () {});
        },
        unbindCustomer(row) {
            CustomerService.unbindHighValueCustomer([row.id]).then(res => {
                if (res.code == 0) {
                    this.$message.success('解除绑定成功');
                    this.boundList = this.boundList.filter(function (item) { return item.id !== row.id; });
                }
            });
        },
        goFollow() {
            const url = this.actions.follow_url || ('/#/customers/view?id=' + this.$route.query.id);
            this.openHash(url);
        },
        switchCustomer(id) {
            if (!id) return;
            if (!this.neighbors.switch_blocked) {
                this.$router.replace({path: '/customers/insight', query: {id: id}});
                return;
            }
            CustomerService.getHighValueView(this.$route.query.id).then(res => {
                if (res.code == 0 && res.data) {
                    this.$set(this.info, 'neighbors', res.data.neighbors || {});
                }
                if (this.neighbors.switch_blocked) {
                    this.$message.warning(this.neighbors.switch_message || '请先填写跟进记录，更新跟进记录后方可查看后续高价值客户');
                    return;
                }
                this.$router.replace({path: '/customers/insight', query: {id: id}});
            });
        },
        showMobile(item) {
            CustomerService.getHighValueContactMobile(this.$route.query.id, item.id).then(res => {
                if (res.code == 0 && res.data) {
                    this.$set(item, 'display_mobile', res.data.mobile);
                    const customerName = this.header.customer_name || '';
                    const contactName = item.name || '联系人';
                    this.$set(this.info, 'neighbors', Object.assign({}, this.neighbors, {
                        switch_blocked: true,
                        switch_message: '请先填写【' + customerName + '】【' + contactName + '】的跟进记录，更新跟进记录后方可查看后续高价值客户'
                    }));
                }
            });
        },
        openAssistant() {
            this.assistantVisible = true;
            this.loadAssistantHistory();
        },
        closeAssistant() {
            this.assistantVisible = false;
            this.assistantPinned = false;
            this.assistantHistoryVisible = false;
        },
        resetAssistant(reload) {
            this.assistantSessionId = 0;
            this.assistantInput = '';
            this.assistantMessages = [];
            this.assistantSending = false;
            this.assistantHistoryVisible = false;
            if (reload && this.assistantVisible) {
                this.loadAssistantHistory();
            }
        },
        loadAssistantHistory() {
            const id = this.$route.query.id;
            if (!id) return;
            CustomerService.getHighValueAssistantHistory(id).then(res => {
                if (res.code == 0 && res.data) {
                    this.assistantSessionId = (res.data.session && res.data.session.id) || 0;
                    this.assistantMessages = res.data.messages || [];
                    this.assistantSessions = res.data.sessions || [];
                    this.$nextTick(this.scrollAssistant);
                }
            });
        },
        newAssistantSession() {
            const id = this.$route.query.id;
            CustomerService.newHighValueAssistantSession(id).then(res => {
                if (res.code == 0) {
                    this.assistantSessionId = 0;
                    this.assistantMessages = [];
                    this.assistantSessions = (res.data && res.data.sessions) || this.assistantSessions;
                    this.assistantHistoryVisible = false;
                    this.$message.success('已新开对话，历史会话已保存');
                }
            });
        },
        toggleAssistantHistory() {
            this.assistantHistoryVisible = !this.assistantHistoryVisible;
            if (this.assistantHistoryVisible) {
                this.loadAssistantHistoryList();
            }
        },
        loadAssistantHistoryList() {
            const id = this.$route.query.id;
            if (!id) return;
            CustomerService.getHighValueAssistantHistory(id, this.assistantSessionId || undefined).then(res => {
                if (res.code == 0 && res.data) {
                    this.assistantSessions = res.data.sessions || [];
                }
            });
        },
        openAssistantSession(item) {
            if (!item || !item.id) return;
            CustomerService.getHighValueAssistantHistory(this.$route.query.id, item.id).then(res => {
                if (res.code == 0 && res.data) {
                    this.assistantSessionId = (res.data.session && res.data.session.id) || item.id;
                    this.assistantMessages = res.data.messages || [];
                    this.assistantSessions = res.data.sessions || this.assistantSessions;
                    this.assistantHistoryVisible = false;
                    this.$nextTick(this.scrollAssistant);
                }
            });
        },
        formatAssistantTime(ts) {
            const n = parseInt(ts, 10);
            if (!n) return '';
            const d = new Date(n * 1000);
            const pad = function (v) { return v < 10 ? '0' + v : '' + v; };
            return (d.getMonth() + 1) + '-' + pad(d.getDate()) + ' ' + pad(d.getHours()) + ':' + pad(d.getMinutes());
        },
        sendStrategy() {
            this.sendAssistant('strategy');
        },
        onAssistantKeydown(e) {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                this.sendAssistant();
            }
        },
        sendAssistant(action) {
            const content = String(this.assistantInput || '').trim();
            const isStrategy = action === 'strategy';
            if (this.assistantSending) return;
            if (!isStrategy && !content) {
                this.$message.warning('请输入要咨询的内容');
                return;
            }
            const userText = isStrategy ? (content || '沟通策略') : content;
            const localKey = 'tmp-' + Date.now();
            this.assistantMessages.push({
                _key: localKey,
                role: 'user',
                content: userText
            });
            this.assistantInput = '';
            this.assistantSending = true;
            this.$nextTick(this.scrollAssistant);
            CustomerService.chatHighValueAssistant({
                id: this.$route.query.id,
                session_id: this.assistantSessionId || 0,
                message: isStrategy ? content : userText,
                action: isStrategy ? 'strategy' : ''
            }).then(res => {
                this.assistantSending = false;
                if (res.code == 0 && res.data) {
                    if (res.data.session && res.data.session.id) {
                        this.assistantSessionId = res.data.session.id;
                    }
                    if (res.data.message) {
                        this.assistantMessages.push(res.data.message);
                    }
                    this.loadAssistantHistoryList();
                } else {
                    this.assistantMessages.push({
                        _key: 'err-' + Date.now(),
                        role: 'assistant',
                        content: res.msg || 'AI 服务暂不可用，请稍后重试'
                    });
                }
                this.$nextTick(this.scrollAssistant);
            }).catch(() => {
                this.assistantSending = false;
                this.assistantMessages.push({
                    _key: 'err-' + Date.now(),
                    role: 'assistant',
                    content: 'AI 服务暂不可用，请稍后重试'
                });
                this.$nextTick(this.scrollAssistant);
            });
        },
        copyAssistant(msg) {
            const text = (msg && (msg.copy_text || msg.content)) || '';
            if (!text) return;
            const done = () => this.$message.success('已复制');
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text).then(done).catch(() => this.fallbackCopy(text, done));
            } else {
                this.fallbackCopy(text, done);
            }
        },
        fallbackCopy(text, done) {
            const input = document.createElement('textarea');
            input.value = text;
            document.body.appendChild(input);
            input.select();
            try {
                document.execCommand('copy');
                done();
            } catch (e) {
                this.$message.error('复制失败');
            }
            document.body.removeChild(input);
        },
        scrollAssistant() {
            const el = this.$refs.assistantMsgs;
            if (el) el.scrollTop = el.scrollHeight;
        },
        openUrl(url) {
            if (url) window.open(url, '_blank');
        },
        openHash(url) {
            if (!url) return;
            window.open(url, '_blank');
        },
        goDoneCustomers() {
            const nearby = this.nearby || {};
            const header = this.header || {};
            const query = {};
            const provinceId = nearby.province_id || header.province_id;
            const cityId = nearby.city_id || header.city_id;
            if (provinceId) {
                query.province_id = provinceId;
            }
            if (cityId) {
                query.city_id = cityId;
            }
            const route = this.$router.resolve({
                path: '/customers/done',
                query: query
            });
            window.open(route.href, '_blank');
        },
        goProductInfo() {
            const route = this.$router.resolve({
                path: '/customers/view',
                query: {
                    id: this.$route.query.id,
                    tab: 'product'
                }
            });
            window.open(route.href, '_blank');
        }
    }
}
</script>

<style scoped>
.insight-page { background: #f5f7fb; }

.insight-page.assistant-pinned { padding-right: 380px; }
.insight-page.assistant-pinned .float-ops { right: 398px; }
.insight-wrap { padding: 12px 24px 24px; }
.insight-crumb { color: #909399; font-size: 13px; margin-bottom: 14px; }
.insight-crumb i { margin: 0 6px; font-size: 12px; color: #c0c4cc; }
.insight-crumb a { color: #909399; text-decoration: none; }
.crumb-current { color: #606266; }
.insight-kicker { color: #8b8fd9; font-size: 12px; letter-spacing: 1px; margin-bottom: 8px; }
.insight-kicker i { margin-right: 4px; }
.insight-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 14px; }
.title-wrap { display: flex; align-items: center; min-width: 0; }
.title-mark { width: 28px; height: 28px; border-radius: 8px; background: linear-gradient(135deg, #7c6cf0, #5b8def); color: #fff; display: inline-flex; align-items: center; justify-content: center; margin-right: 8px; }
.page-title { font-size: 22px; font-weight: 700; color: #303133; }
.customer-name { font-size: 22px; font-weight: 700; color: #3b82f6; margin-left: 10px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.top-actions { display: flex; align-items: center; flex-shrink: 0; }
.top-actions .el-button { margin-left: 0; }
.top-actions .el-button + .el-button { margin-left: 8px; }
.btn-claim { background: #f59e0b !important; border-color: #f59e0b !important; color: #fff !important; }
.btn-follow { background: #22c55e !important; border-color: #22c55e !important; color: #fff !important; }
.insight-banner { display: flex; justify-content: space-between; align-items: center; background: #eef2ff; border-radius: 10px; padding: 12px 16px; margin-bottom: 16px; color: #606266; }
.insight-banner.is-generating { background: #eef2ff; border: 1px solid #c7d2fe; }
.insight-banner.is-failed { background: #fef2f2; border: 1px solid #fecaca; color: #b91c1c; }
.banner-generating { color: #4f46e5; font-weight: 600; display: inline-flex; align-items: center; }
.banner-generating i { margin-right: 6px; }
.banner-left { display: flex; align-items: center; }
.banner-icon { width: 28px; height: 28px; border-radius: 50%; background: #fff; color: #7c6cf0; display: inline-flex; align-items: center; justify-content: center; margin-right: 10px; }
.banner-left em { font-style: normal; color: #4f46e5; font-weight: 600; }
.banner-right { display: flex; align-items: center; color: #909399; font-size: 12px; white-space: nowrap; }
.banner-refresh { margin-left: 6px; color: #7c6cf0 !important; font-size: 16px; }
.overview-cards { display: flex; gap: 12px; margin-bottom: 16px; }
.ov-card { flex: 1; background: #fff; border-radius: 12px; padding: 18px 16px; display: flex; align-items: flex-start; position: relative; overflow: hidden; box-shadow: 0 1px 2px rgba(0,0,0,.04); }
.ov-card:after { content: ''; position: absolute; right: -24px; top: -24px; width: 90px; height: 90px; border-radius: 50%; opacity: .16; }
.ov-scale:after { background: #60a5fa; }
.ov-intention:after { background: #a78bfa; }
.ov-expire:after { background: #fbbf24; }
.ov-icon { width: 40px; height: 40px; border-radius: 12px; display: flex; align-items: center; justify-content: center; margin-right: 12px; font-size: 18px; color: #fff; flex-shrink: 0; }
.ov-scale .ov-icon { background: #3b82f6; }
.ov-intention .ov-icon { background: #7c3aed; }
.ov-expire .ov-icon { background: #f59e0b; }
.ov-name { color: #909399; margin-bottom: 8px; font-size: 13px; display: flex; align-items: center; }
.ai-badge { margin-left: 8px; background: #fb923c; color: #fff; border-radius: 10px; font-size: 11px; padding: 1px 6px; line-height: 16px; }
.ai-wait-badge { margin-left: 8px; background: #eef2ff; color: #4f46e5; border-radius: 10px; font-size: 11px; padding: 1px 6px; line-height: 16px; }
.ov-value { font-size: 28px; font-weight: 700; color: #303133; line-height: 1.1; }
.ov-value em { font-style: normal; font-size: 14px; margin-left: 4px; color: #909399; font-weight: 500; }
.ov-extra { color: #909399; margin-top: 8px; font-size: 12px; }
.insight-body { display: flex; gap: 16px; }
.insight-nav { width: 168px; flex-shrink: 0; background: #fff; border-radius: 12px; padding: 10px 8px; height: fit-content; position: sticky; top: 16px; }
.nav-item { display: flex; align-items: center; justify-content: flex-start; gap: 8px; padding: 10px 12px; cursor: pointer; color: #606266; border-radius: 8px; font-size: 13px; margin-bottom: 4px; }
.nav-item i { margin: 0; font-size: 15px; flex: 0 0 auto; width: auto; }
.nav-item span { margin: 0; padding: 0; flex: 0 0 auto; white-space: nowrap; }
.nav-item.active { color: #6366f1; background: #eef2ff; font-weight: 600; }
.insight-main { flex: 1; min-width: 0; padding-right: 8px; }
.mod-card { background: #fff; border-radius: 12px; padding: 16px 18px 18px; margin-bottom: 16px; position: relative; overflow: hidden; box-shadow: 0 1px 2px rgba(0,0,0,.04); }
.mod-card:before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 4px; }
.mod-overview:before { background: #3b82f6; }
.mod-contacts:before { background: #22c55e; }
.mod-nearby:before { background: #8b5cf6; }
.mod-trial:before { background: #f59e0b; }
.mod-signals:before { background: #6366f1; }
.mod-strategy:before { background: #8b5cf6; }
.mod-head { display: flex; align-items: center; justify-content: flex-start; gap: 8px; margin-bottom: 14px; width: 100%; }
.mod-head-left { display: flex; align-items: center; justify-content: flex-start; gap: 8px; flex: 0 0 auto; width: auto; margin: 0; }
.mod-icon { width: 26px; height: 26px; border-radius: 8px; display: inline-flex; align-items: center; justify-content: center; color: #fff; margin: 0; font-size: 14px; flex: 0 0 26px; }
.mod-overview .mod-icon { background: #3b82f6; }
.mod-contacts .mod-icon { background: #22c55e; }
.mod-nearby .mod-icon { background: #8b5cf6; }
.mod-trial .mod-icon { background: #f59e0b; }
.mod-signals .mod-icon { background: #6366f1; }
.mod-strategy .mod-icon { background: #8b5cf6; }
.mod-name { font-size: 16px; font-weight: 700; color: #303133; margin: 0; padding: 0; flex: 0 0 auto; white-space: nowrap; line-height: 26px; }
.mod-head-left .ai-badge, .mod-head-left .ai-wait-badge { margin-left: 0; }
.mod-link { margin-left: 0; color: #6366f1; font-size: 13px; font-weight: 500; text-decoration: none; cursor: pointer; }
.mod-link:after { content: ' ↗'; font-size: 12px; }
.ov-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
.ov-table th, .ov-table td { border: 1px solid #ebeef5; padding: 8px 10px; font-size: 12px; }
.ov-table th { width: 92px; background: #f5f7fa; color: #909399; font-weight: 500; }
.ov-table td { color: #303133; }
.contact-list, .famous-list { display: flex; gap: 14px; overflow-x: auto; padding-bottom: 4px; }
.contact-card { width: 300px; min-width: 300px; max-width: 300px; flex: 0 0 300px; border: 1px solid #ebeef5; border-radius: 14px; padding: 16px; background: #fff; box-shadow: 0 1px 2px rgba(15,23,42,.04); }
.role-decision { border-top: 3px solid #f87171; }
.role-influencer { border-top: 3px solid #fbbf24; }
.role-executor { border-top: 3px solid #34d399; }
.contact-head { display: flex; align-items: center; gap: 6px; }
.contact-name { font-weight: 700; font-size: 15px; }
.role-tag { margin-left: auto; font-size: 12px; border-radius: 10px; padding: 1px 8px; }
.role-decision .role-tag { color: #ef4444; background: #fef2f2; }
.role-influencer .role-tag { color: #d97706; background: #fffbeb; }
.role-executor .role-tag { color: #059669; background: #ecfdf5; }
.contact-job { color: #909399; font-size: 12px; margin: 6px 0 10px; }
.phone-box { background: #f8fafc; border-radius: 8px; padding: 8px 10px; color: #606266; font-size: 13px; display: flex; align-items: center; }
.phone-box i { margin-right: 6px; color: #94a3b8; }
.phone-box .el-icon-view { margin-left: auto; margin-right: 0; cursor: pointer; color: #64748b; }
.contact-meta { display: flex; justify-content: space-between; align-items: center; margin-top: 10px; font-size: 12px; }
.mini-chip { display: inline-block; background: #eef2ff; color: #4f46e5; border-radius: 10px; padding: 1px 8px; margin-right: 4px; }
.meta-date { color: #94a3b8; }
.attitude-bar { margin-top: 10px; background: #eef2ff; border-radius: 8px; padding: 8px 10px; display: flex; justify-content: space-between; font-size: 12px; color: #334155; }
.timeline-box { margin-top: 10px; border: 1px solid #e2e8f0; border-radius: 10px; background: #f8fafc; overflow: hidden; }
.timeline { margin-top: 0; overflow-y: auto; padding: 8px 10px 4px; }
.timeline-resizer { height: 10px; cursor: ns-resize; background: linear-gradient(#e2e8f0, #cbd5e1); position: relative; }
.timeline-resizer:before { content: ''; position: absolute; left: 50%; top: 3px; width: 28px; height: 3px; margin-left: -14px; border-radius: 2px; background: #94a3b8; }
.timeline-item { display: flex; align-items: flex-start; gap: 6px; font-size: 12px; color: #64748b; margin-bottom: 6px; }
.timeline-item .dot { width: 8px; height: 8px; border-radius: 50%; background: #818cf8; margin-top: 5px; flex: 0 0 8px; }
.nearby-panels { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 16px; }
.nearby-panel { border: 1px solid #dbeafe; border-radius: 8px; overflow: hidden; background: #fff; }
.nearby-panel-head { background: #e8f3ff; color: #2563eb; font-size: 14px; font-weight: 600; padding: 12px 16px; }
.nearby-panel-body { max-height: 220px; overflow-y: auto; }
.nearby-panel-body::-webkit-scrollbar { width: 6px; }
.nearby-panel-body::-webkit-scrollbar-thumb { background: #c7d2fe; border-radius: 6px; }
.nearby-panel-row { display: flex; justify-content: space-between; align-items: center; padding: 12px 16px; border-top: 1px solid #eef2f7; font-size: 14px; color: #334155; }
.nearby-panel-row em { font-style: normal; }
.nearby-panel-row span { color: #2563eb; font-weight: 600; }
@media (max-width: 1100px) { .nearby-panels { grid-template-columns: 1fr; } }
.sub-title { font-weight: 700; margin-bottom: 10px; color: #303133; }
.famous-card { min-width: 250px; flex: 1; border: 1px solid #eef2f7; border-radius: 14px; padding: 14px 16px; background: #fff; box-shadow: 0 1px 2px rgba(15,23,42,.04); }
.famous-head { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; margin-bottom: 8px; }
.school-tag { border-radius: 4px; font-size: 11px; padding: 2px 6px; background: #eef2ff; color: #4f46e5; }
.product-tag { border-radius: 4px; font-size: 11px; padding: 2px 6px; }
.famous-line { color: #64748b; font-size: 12px; line-height: 1.8; }
.trial-layout { display: flex; gap: 16px; }
.trial-table-wrap { flex: 1.2; background: #ecfdf5; border-radius: 10px; padding: 12px; }
.trial-caption { color: #047857; font-weight: 600; margin-bottom: 8px; }
.trial-valid { margin-left: 8px; font-size: 12px; font-weight: 600; border-radius: 10px; padding: 1px 8px; }
.trial-valid.is-valid { background: #dcfce7; color: #15803d; }
.trial-valid.is-invalid { background: #fef2f2; color: #b91c1c; }
.trial-table { width: 100%; border-collapse: collapse; }
.trial-table th, .trial-table td { padding: 8px; font-size: 12px; color: #065f46; text-align: left; }
.trial-table th { font-weight: 600; }
.trial-chart { flex: 1; min-width: 260px; }
.chart-title { color: #64748b; font-size: 12px; margin-bottom: 8px; }
.line-svg { width: 100%; height: 150px; }
.chart-axis { display: flex; justify-content: space-between; color: #94a3b8; font-size: 12px; }
.signal-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.signal-box { position: relative; border-radius: 12px; padding: 14px 16px; min-height: 108px; }
.ai-corner { position: absolute; left: 8px; top: 8px; margin-left: 0; }
.signal-coop, .signal-comp { background: #ecfdf5; }
.signal-need { background: #eef2ff; }
.signal-block { background: #fef2f2; }
.signal-title { font-weight: 700; margin-bottom: 8px; color: #303133; }
.chips { display: flex; flex-wrap: wrap; gap: 8px; }
.chip { display: inline-flex; align-items: center; border-radius: 14px; padding: 5px 10px; font-size: 12px; background: #fff; }
.chip-green { background: #fff; color: #047857; }
.chip-purple { background: #fff; color: #4f46e5; }
.chip-red { background: #fff; color: #b91c1c; }
.strategy-item { position: relative; display: flex; gap: 10px; border-radius: 10px; padding: 12px; margin-bottom: 10px; }
.strategy-0 { background: #eef2ff; }
.strategy-1 { background: #ecfdf5; }
.strategy-2 { background: #fff7ed; }
.strategy-no { width: 22px; height: 22px; border-radius: 50%; background: #6366f1; color: #fff; display: inline-flex; align-items: center; justify-content: center; font-size: 12px; flex-shrink: 0; }
.strategy-1 .strategy-no { background: #10b981; }
.strategy-2 .strategy-no { background: #f59e0b; }
.strategy-item p { margin: 6px 0 0; color: #64748b; font-size: 13px; }
.empty-box { color: #909399; background: #f8fafc; padding: 16px; border-radius: 8px; text-align: center; }
.muted { color: #909399; font-size: 12px; line-height: 1.6; }
.float-ops { position: fixed; right: 18px; top: 46%; z-index: 20; display: flex; flex-direction: column; align-items: flex-end; }
.fab { color: #fff; border-radius: 18px 0 0 18px; padding: 10px 14px; margin-bottom: 8px; cursor: pointer; font-size: 13px; box-shadow: 0 4px 10px rgba(0,0,0,.12); }
.fab i { margin-right: 4px; }
.fab-claim { background: #f59e0b; }
.fab-follow { background: #22c55e; }
.fab-ai { background: #7c3aed; }
.fab-switch { background: #fff; border-radius: 16px; box-shadow: 0 4px 10px rgba(0,0,0,.08); overflow: hidden; }
.fab-arrow { width: 42px; height: 32px; display: flex; align-items: center; justify-content: center; cursor: pointer; color: #64748b; }
.fab-arrow + .fab-arrow { border-top: 1px solid #eef2f7; }
.fab-arrow.disabled { color: #d1d5db; cursor: not-allowed; }

.assistant-panel {
    position: fixed;
    right: 0;
    top: 0;
    bottom: 0;
    width: 380px;
    background: #fff;
    box-shadow: -8px 0 24px rgba(15,23,42,.12);
    z-index: 30;
    display: flex;
    flex-direction: column;
}
.assistant-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    border-bottom: 1px solid #eef2f7;
}
.assistant-title { font-weight: 700; color: #312e81; font-size: 15px; }
.assistant-title i { margin-right: 6px; color: #7c3aed; }
.assistant-head-ops i { margin-left: 10px; cursor: pointer; color: #64748b; font-size: 16px; }
.assistant-head-ops i:hover { color: #7c3aed; }
.assistant-history { max-height: 260px; overflow-y: auto; border-bottom: 1px solid #eef2f7; background: #fff; }
.history-head { padding: 10px 16px 6px; font-size: 12px; color: #94a3b8; }
.history-empty { padding: 8px 16px 14px; color: #94a3b8; font-size: 12px; }
.history-item { padding: 8px 16px; cursor: pointer; }
.history-item:hover, .history-item.active { background: #eef2ff; }
.history-title { font-size: 13px; color: #334155; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.history-time { font-size: 12px; color: #94a3b8; margin-top: 2px; }
.assistant-msgs { flex: 1; overflow-y: auto; padding: 16px; background: #f8fafc; }
.assistant-welcome { background: #eef2ff; color: #4338ca; border-radius: 12px; padding: 12px 14px; line-height: 1.7; font-size: 13px; margin-bottom: 14px; }
.assistant-msg { display: flex; margin-bottom: 12px; }
.msg-user { justify-content: flex-end; }
.msg-bubble { max-width: 86%; border-radius: 12px; padding: 10px 12px; background: #fff; box-shadow: 0 1px 2px rgba(15,23,42,.06); }
.msg-user .msg-bubble { background: #7c3aed; color: #fff; }
.msg-text { white-space: pre-wrap; line-height: 1.7; font-size: 13px; color: inherit; }
.msg-user .msg-text { color: #fff; }
.msg-assistant .el-button { padding: 0; margin-top: 6px; color: #7c3aed; }
.thinking { color: #64748b; }
.assistant-foot { padding: 12px; border-top: 1px solid #eef2f7; background: #fff; }
.assistant-input { display: flex; gap: 8px; margin-top: 8px; align-items: flex-end; }
.assistant-input .el-textarea { flex: 1; }

.attitude-bar em { font-style: normal; margin-right: 2px; }
.attitude-bar b { color: #2563eb; font-weight: 700; }
.tl-label { color: #4f46e5; font-weight: 600; white-space: nowrap; flex: 0 0 auto; }
.tl-time { color: #94a3b8; white-space: nowrap; flex: 0 0 auto; }
.tl-text { color: #64748b; flex: 1 1 auto; min-width: 0; word-break: break-word; line-height: 1.5; }
.sub-ico { margin-right: 6px; }
.famous-head { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; margin-bottom: 10px; }
.famous-head strong { max-width: 120px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #0f172a; }
.tag-city { background: #dbeafe; color: #2563eb; }
.tag-district { background: #fee2e2; color: #dc2626; }
.tag-school { background: #eef2ff; color: #4f46e5; }
.tag-prod-blue { background: #dbeafe; color: #2563eb; }
.tag-prod-cyan { background: #cffafe; color: #0e7490; }
.tag-prod-green { background: #dcfce7; color: #15803d; }
.chip i { width: 6px; height: 6px; border-radius: 50%; margin-right: 6px; display: inline-block; }
.chip-green i { background: #22c55e; }
.chip-purple i { background: #6366f1; }
.chip-red i { background: #ef4444; }
.chip-green { color: #047857; }
.chip-purple { color: #4f46e5; }
.chip-red { color: #b91c1c; }

</style>



// WEBPACK FOOTER //
// src/views/customer/insight.vue